import React, { useEffect, useRef, useState } from "react";

import { Box } from "@material-ui/core";

import { Button, useNotify } from "react-admin";

import { AddressAutocomplete } from "../../../components/common/AddressAutocomplete";
import { AddressDialog } from "../../../components/common/AddressDialog";
import { EditIcon } from "../../../components/common/iconography";
import { isSubscriptionDisabled, logError } from "../../../utils/appUtils";
import { isAddressEqual, isEmptyAddress } from "../../../utils/utils";
import { updateSavedAddressRecord } from "../accounts/AccountUtils";
import { handleShipToSelect } from "./OutboundUtils";

export const AddressBookSelect = ({
  addressBook,
  form,
  formData,
  selectedAddress,
  setSelectedAddress,
  onAddressUpdated,
}) => {
  const notify = useNotify();
  const [selectedBookEntry, setSelectedBookEntry] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const prefilledForIdRef = useRef(null);

  // On an existing outbound whose shipTo matches an address-book entry,
  // pre-select that entry so EDIT is enabled and the shipTo fields render
  // read-only. Only in edit mode (formData.id present) — on create, typing a
  // full manual address that happens to match the book must not auto-lock the
  // fields mid-entry. We set selectedAddress to the existing shipTo (not via
  // handleShipToSelect) so the persisted snapshot is left untouched; the match
  // is a full-field equality (isAddressEqual compares phone too), so the
  // selector and the locked shipTo fields always describe the same record.
  //
  // The latch is keyed to the outbound id and only trips once a match is found:
  // opening via in-app navigation serves the cached list record first and then
  // refetches the full record (which re-scopes the address book via
  // billToAccountId), so the address book and shipTo can be transiently
  // mismatched. Latching only on a confirmed match lets the effect keep
  // retrying until the final data lands; keying to the id re-evaluates per
  // shipment. Once matched (ref === id) a manual clear is respected — it won't
  // re-lock.
  useEffect(() => {
    if (selectedBookEntry) return;
    if (!formData?.id) return;
    if (prefilledForIdRef.current === formData.id) return;
    if (!addressBook || addressBook.length === 0) return;
    const shipTo = formData?.shipTo;
    if (isEmptyAddress(shipTo)) return;
    const match = addressBook.find((a) => isAddressEqual(a, shipTo));
    if (match) {
      prefilledForIdRef.current = formData.id;
      setSelectedBookEntry({ ...match });
      setSelectedAddress(shipTo);
    }
  }, [addressBook, formData, selectedBookEntry, setSelectedAddress]);

  // When the account (and thus the address book) changes, drop a selection that
  // no longer belongs to the current book so EDIT can't act on the previous
  // account's address. A saved edit keeps the same id, so this leaves it intact.
  // Also clear the ship-to it produced, otherwise the form keeps the previous
  // account's address (Autocomplete value + hidden manual input + stale shipTo).
  useEffect(() => {
    if (
      selectedBookEntry &&
      !addressBook?.some((a) => a.id === selectedBookEntry.id)
    ) {
      setSelectedBookEntry(null);
      handleShipToSelect(null, form, setSelectedAddress);
      // Also drop any open edit dialog + its draft so a confirm can't act on
      // (or leave on the form) the previous account's record.
      setIsOpen(false);
      form.change("savedAddress", undefined);
    }
  }, [addressBook, selectedBookEntry, form, setSelectedAddress]);

  if (!addressBook || addressBook.length === 0) return null;

  const handleSelect = (event, newValue) => {
    // Capture the pristine record (with id) before handleShipToSelect strips it.
    setSelectedBookEntry(newValue ? { ...newValue } : null);
    handleShipToSelect(newValue, form, setSelectedAddress);
  };

  const handleEdit = () => {
    form.change("savedAddress", selectedBookEntry);
    setIsOpen(true);
  };

  const handleClose = () => {
    form.change("savedAddress", undefined);
    setIsOpen(false);
  };

  const handleSave = async () => {
    try {
      const updated = await updateSavedAddressRecord(
        form.getState().values.savedAddress,
      );
      if (!updated) {
        logError(
          "outboundShipments:AddressBookSelect:handleSave",
          new Error("updateSavedAddress returned no data"),
          notify,
        );
        return;
      }
      notify("Address updated successfully.", "success");

      // Refresh the parent's address book list (full record, with id) so the
      // selector label updates, and keep the entry available for further edits.
      onAddressUpdated && onAddressUpdated({ ...updated });
      setSelectedBookEntry({ ...updated });

      // Reflect the change in the outbound form (shipTo + selectedAddress).
      handleShipToSelect({ ...updated }, form, setSelectedAddress);

      form.change("savedAddress", undefined);
      setIsOpen(false);
    } catch (error) {
      logError("outboundShipments:AddressBookSelect:handleSave", error, notify);
    }
  };

  return (
    <Box display="flex" alignItems="flex-start">
      <Box flexGrow={1}>
        <AddressAutocomplete
          options={addressBook}
          value={selectedAddress}
          onChange={handleSelect}
        />
      </Box>
      <Box ml={1} mt={1}>
        <Button
          label="Edit"
          color="primary"
          onClick={handleEdit}
          disabled={!selectedBookEntry || isSubscriptionDisabled()}
        >
          <EditIcon />
        </Button>
      </Box>
      {isOpen && (
        <AddressDialog
          open={isOpen}
          title="Edit Address"
          formData={formData}
          onSave={handleSave}
          onClose={handleClose}
        />
      )}
    </Box>
  );
};
